import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, FileText, Loader2, Sparkles, AlertCircle } from "lucide-react";

export default function EstimateUpload() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const [repairType, setRepairType] = useState("body_shop_collision");
  const [collisionDamage, setCollisionDamage] = useState(true);
  const [postWheelAlign, setPostWheelAlign] = useState(true);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  };

  const submit = async () => {
    if (!file) return toast.error("Pick or drop a PDF first");
    setUploading(true);
    setProgress("Uploading file…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("repair_type", repairType);
      fd.append("collision_damage", collisionDamage);
      fd.append("post_wheel_alignment", postWheelAlign);
      setProgress("AI extracting data (OpenAI Vision)…");
      const r = await api.post("/estimate-analyzer/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success(`Report generated · ${r.data.report.sections.flatMap(s => s.cards).length} operations identified`);
      navigate(`/calibration-reports/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress("");
    }
  };

  return (
    <div className="space-y-6 animate-slide-up max-w-4xl" data-testid="estimate-upload-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Calibration Reports</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">New Report From Estimate</h1>
        <p className="text-sm text-slate-400 mt-1.5 max-w-2xl">
          Upload a body shop estimate PDF (CCC ONE, Mitchell, Audatex). AI reads every line and the OEM rules engine
          identifies which ADAS calibrations, scans, and safety inspections the manufacturer requires.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Drop zone */}
        <div className="lg:col-span-2">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={`rounded-xl border-2 border-dashed transition-all p-12 text-center ${
              dragging ? "border-blue-500 bg-blue-500/5" : "border-slate-700 bg-[#0B1120]"
            }`}
            data-testid="estimate-dropzone"
          >
            {file ? (
              <div>
                <FileText className="w-12 h-12 text-blue-400 mx-auto mb-3" />
                <div className="font-mono text-sm text-slate-200">{file.name}</div>
                <div className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB</div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-3 text-slate-400"
                  onClick={() => setFile(null)}
                  data-testid="estimate-clear-file"
                >
                  Choose different file
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <div className="text-sm text-slate-300 font-medium">Drop estimate PDF here</div>
                <div className="text-xs text-slate-500 mt-1.5">or click below to browse · max 25MB</div>
                <input
                  id="est-file-input"
                  type="file"
                  accept=".pdf,image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
                  data-testid="estimate-file-input"
                />
                <Button
                  variant="outline"
                  className="mt-4 border-slate-700"
                  onClick={() => document.getElementById("est-file-input").click()}
                  data-testid="estimate-browse-btn"
                >
                  Browse file
                </Button>
              </>
            )}
          </div>

          <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex gap-2.5 text-amber-200 text-xs">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-0.5">AI Vision pricing</div>
              <div className="text-amber-300/80">Uses OpenAI GPT-4o via your Emergent key. Typical estimate (5-10 pages) costs $0.02-0.05.</div>
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Repair Type</div>
            <div className="space-y-2">
              <button
                onClick={() => setRepairType("body_shop_collision")}
                data-testid="repair-type-collision"
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  repairType === "body_shop_collision"
                    ? "border-blue-500/50 bg-blue-500/10 text-blue-200"
                    : "border-slate-800 hover:border-slate-700 text-slate-300"
                }`}
              >
                <div className="text-sm font-semibold">Body Shop Collision</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Full report — ADAS + scans + safety + alignment</div>
              </button>
              <button
                onClick={() => setRepairType("windshield_only")}
                data-testid="repair-type-windshield"
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  repairType === "windshield_only"
                    ? "border-blue-500/50 bg-blue-500/10 text-blue-200"
                    : "border-slate-800 hover:border-slate-700 text-slate-300"
                }`}
              >
                <div className="text-sm font-semibold">Windshield Only</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Just the front camera calibration</div>
              </button>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Vehicle Status</div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Collision Damage</Label>
                <Switch
                  checked={collisionDamage}
                  onCheckedChange={setCollisionDamage}
                  data-testid="toggle-collision-damage"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Post Wheel Alignment</Label>
                <Switch
                  checked={postWheelAlign}
                  onCheckedChange={setPostWheelAlign}
                  data-testid="toggle-post-wheel-align"
                />
              </div>
            </div>
          </div>

          <Button
            data-testid="generate-report-btn"
            onClick={submit}
            disabled={!file || uploading}
            className="w-full bg-blue-600 hover:bg-blue-500"
            size="lg"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{progress || "Analyzing…"}</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" />Generate Calibration Report</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
